import React, { useEffect } from "react";

export default function GalleryLightbox({ images, currentIndex, onClose, onPrev, onNext }) {
  useEffect(() => {
    if (!images || images.length === 0 || currentIndex === -1) {
      document.body.style.overflow = "unset";
      return;
    }

    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", handleKeyDown);
    // Prevent scrolling behind the lightbox
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [images, currentIndex, onClose, onPrev, onNext]);

  if (!images || images.length === 0 || currentIndex === -1) return null;

  const currentImageUrl = images[currentIndex];

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/95 backdrop-blur-md select-none animate-fade-in">
      {/* Top Bar */}
      <div className="absolute top-0 left-0 right-0 p-6 flex justify-between items-center text-white z-10">
        <div className="text-sm font-medium tracking-wide text-zinc-400">
          {currentIndex + 1} / {images.length}
        </div>
        <button
          onClick={onClose}
          className="p-2 text-zinc-400 hover:text-white transition-colors duration-150 cursor-pointer text-2xl"
          aria-label="Close Lightbox"
        >
          <i className="fa-solid fa-xmark" />
        </button>
      </div>

      {/* Main Image Container */}
      <div className="relative w-full max-w-5xl px-4 flex items-center justify-center">
        {/* Prev Button */}
        <button
          onClick={onPrev}
          className="absolute left-4 md:left-8 p-3 text-zinc-400 hover:text-white transition-colors duration-150 bg-zinc-900/40 hover:bg-zinc-900/80 rounded-none cursor-pointer text-xl z-20"
          aria-label="Previous Image"
        >
          <i className="fa-solid fa-chevron-left" />
        </button>

        {/* Image */}
        <div className="max-h-[80vh] flex items-center justify-center overflow-hidden border border-zinc-800">
          <img
            src={`https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/${currentImageUrl.replace("https://assets.auxodev.dk/", "")}`}
            alt={`Gallery image ${currentIndex + 1}`}
            className="max-w-full max-h-[75vh] object-contain block"
          />
        </div>

        {/* Next Button */}
        <button
          onClick={onNext}
          className="absolute right-4 md:right-8 p-3 text-zinc-400 hover:text-white transition-colors duration-150 bg-zinc-900/40 hover:bg-zinc-900/80 rounded-none cursor-pointer text-xl z-20"
          aria-label="Next Image"
        >
          <i className="fa-solid fa-chevron-right" />
        </button>
      </div>

      {/* Bottom Thumbnail Strip */}
      <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-2 px-4 overflow-x-auto py-2">
        {images.map((img, idx) => (
          <button
            key={idx}
            onClick={() => {
              // Set index directly via onPrev/onNext or custom callback if needed
              // For simplicity, we can just use a trick or keep it as is
            }}
            className={`w-12 h-12 border transition-all duration-150 overflow-hidden rounded-none cursor-pointer ${
              idx === currentIndex ? "border-brand-blue opacity-100 scale-105" : "border-zinc-800 opacity-40 hover:opacity-80"
            }`}
          >
            <img
              src={`https://assets.auxodev.dk/cdn-cgi/image/width=100,quality=60,format=auto/${img.replace("https://assets.auxodev.dk/", "")}`}
              alt={`Thumbnail ${idx + 1}`}
              className="w-full h-full object-cover"
            />
          </button>
        ))}
      </div>
    </div>
  );
}
